/**
 * 데이터 노드의 **데이터 정의 참조** — 노드에는 정의 id(`config.dataDefinitionId`)만 저장하고,
 * 경로·형식·연결·테이블·항목 같은 세부 값은 쓸 때 정의에서 읽는다(현장 요청 2026-09-22).
 *
 * 화면의 여러 곳(캔버스 '!'·부제, 데이터처리 매핑, 미리보기, 일괄수정, AI 도구 …)이 노드 설정의 filepath·table 을
 * 그대로 읽는다. 그곳을 하나하나 고치는 대신 **저장소 경계**에서 바꾼다:
 *
 *   · 읽을 때(repo.loadCanvas)  → hydrate: 정의의 값을 채워 넣는다 — 화면은 예전처럼 값이 있는 노드를 본다
 *   · 쓸 때 (repo.saveCanvas)   → dehydrate: 정의가 주인인 칸을 걷어 내고 id 만 남긴다
 *   · 미저장 비교(normalizeCanvas) → dehydrate 한 모양끼리 비교 — 채워 넣은 것은 편집이 아니다
 *   · 정의 목록이 늦게 오거나 정의가 바뀌면 열린 캔버스를 다시 채운다(workflowSlice.hydrateLinkedNodes, 되돌리기 기록 없음)
 *
 * 서버 변환기도 같은 규칙으로 정의를 읽는다(backend services/definition_refs.py) — 필드 목록을 함께 고친다.
 * 정의를 모르면(목록을 아직 못 받았거나 지워졌으면) 걷어 내지 않는다 — 값을 잃지 않게.
 */
import type { DataDefinition } from '../../../store/dataDefinitionsSlice';
import { nodeFromDataDefinition } from './dataDefinitionNode';

type DataNodeType = 'csvData' | 'rdbData';

/** 정의가 주인인 칸 — 노드에 저장하지 않는다 */
export const LINKED_FIELDS: Record<DataNodeType, string[]> = {
  csvData: ['filepath', 'delimiter', 'hasHeader', 'format', 'encoding', 'fieldWidths', 'fieldNames', 'tableColumns'],
  rdbData: ['connection', 'schema', 'table', 'columns', 'tableColumns'],
};
const DEF_TYPE: Record<DataNodeType, string> = { csvData: 'sam', rdbData: 'db' };

export const isDataNode = (n: any): n is { type: DataNodeType; data?: any } => n?.type === 'csvData' || n?.type === 'rdbData';

let known: Map<string, DataDefinition> | null = null;

/** 스토어의 정의 목록을 알려 둔다 — 정의 목록을 받을 때마다 (DefinitionRefsSync) */
export function setKnownDefinitions(defs: DataDefinition[]): void {
  known = new Map(defs.map((d) => [d.id, d]));
}

/** 이 노드가 가리키는 정의 (모르거나 종류가 다르면 undefined) */
export function linkedDefinition(node: any, defs?: DataDefinition[]): DataDefinition | undefined {
  if (!isDataNode(node)) return undefined;
  const id = node.data?.config?.dataDefinitionId;
  if (!id) return undefined;
  const def = defs ? defs.find((d) => d.id === id) : known?.get(id);
  return def && def.type === DEF_TYPE[node.type] ? def : undefined;
}

/** 노드 설정에 정의를 입힌다 — 정의가 주인인 칸만 바뀌고 나머지 설정은 그대로다 */
export function applyDefinition(config: Record<string, any>, def: DataDefinition): Record<string, any> {
  return nodeFromDataDefinition(def, config).config;
}

/** 정의의 값을 채운 노드 (바뀐 게 없으면 같은 객체) */
export function hydrateNode<N>(node: N, defs?: DataDefinition[]): N {
  const def = linkedDefinition(node, defs);
  if (!def) return node;
  const n: any = node;
  const cfg = n.data?.config || {};
  const next = applyDefinition(cfg, def);
  const same = LINKED_FIELDS[n.type as DataNodeType].every((k) => JSON.stringify(cfg[k]) === JSON.stringify(next[k]));
  return same ? node : ({ ...n, data: { ...n.data, config: next } } as N);
}

/** 정의가 주인인 칸을 걷어 낸 노드 — 저장·비교용 (정의를 모르면 그대로) */
export function dehydrateNode<N>(node: N, defs?: DataDefinition[]): N {
  const def = linkedDefinition(node, defs);
  if (!def) return node;
  const n: any = node;
  const cfg = { ...(n.data?.config || {}) };
  LINKED_FIELDS[n.type as DataNodeType].forEach((k) => { delete cfg[k]; });
  return { ...n, data: { ...n.data, config: cfg } } as N;
}

export const hydrateNodes = <N>(nodes: N[] | undefined, defs?: DataDefinition[]): N[] => (nodes || []).map((n) => hydrateNode(n, defs));
export const dehydrateNodes = <N>(nodes: N[] | undefined, defs?: DataDefinition[]): N[] => (nodes || []).map((n) => dehydrateNode(n, defs));
